/**
 * The squares the ground is drawn in.
 *
 * Kept apart from the scene because it is arithmetic and nothing else: how a
 * region divides into meshes, which mesh a tile is in, and how the ground is
 * lit so that two meshes meeting do not show the join. All three are worth
 * being able to check without a graphics card in the room.
 */

import { CHUNK_TILES } from '@inverse/common';

/**
 * How many pieces each tile of ground is drawn in, across and down.
 *
 * Three: the middle of a tile is its own colour and nothing else, and the band
 * where one kind of ground gives way to another is a third of a tile either
 * side of the line between them. Two was enough to put the colour on the right
 * tile but spread the change across the whole of it, which read as a smudge
 * rather than as a path.
 */
export const GROUND_STEPS = 3;

/**
 * How much ground one mesh holds, in tiles.
 *
 * The same size the ground is stored and streamed in, so a square that arrives
 * is a square that is drawn and there is no third arithmetic to keep in step.
 */
export const PIECE_TILES = CHUNK_TILES;

/** One square of ground to draw: where it starts and how much of it is real. */
export interface Piece {
  key: string;
  tx: number;
  tz: number;
  width: number;
  height: number;
}

/**
 * The squares a region is drawn in.
 *
 * The last row and column are short wherever a region is not a whole number of
 * squares across, which is most of them: a region is whatever size somebody
 * made it, and rounding it up would draw ground that is not there.
 */
export function piecesOf(region: { width: number; height: number }): Piece[] {
  const pieces: Piece[] = [];

  for (let tz = 0; tz < region.height; tz += PIECE_TILES) {
    for (let tx = 0; tx < region.width; tx += PIECE_TILES) {
      pieces.push({
        key: `${tx / PIECE_TILES}.${tz / PIECE_TILES}`,
        tx,
        tz,
        width: Math.min(PIECE_TILES, region.width - tx),
        height: Math.min(PIECE_TILES, region.height - tz),
      });
    }
  }

  return pieces;
}

/** Which square a tile is drawn in. */
export function pieceOf(tx: number, tz: number): string {
  return `${Math.floor(tx / PIECE_TILES)}.${Math.floor(tz / PIECE_TILES)}`;
}

/**
 * Normals worked out from the ground rather than from the mesh.
 *
 * Three.js works a vertex normal out by averaging the faces that touch it in
 * the geometry it is given. On the join between two squares that is half the
 * faces, so both sides tilt away from the truth in opposite directions and the
 * join lights up as a crease running across the landscape.
 *
 * This averages the faces that touch a vertex in the whole ground, including
 * the ones that belong to the square next door, so a vertex is lit the same way
 * whichever mesh it happens to be in. Faces beyond the edge of the region are
 * left out, because out there is nothing rather than flat.
 */
export function normalsFor(
  region: { width: number; height: number },
  piece: Piece,
  groundAt: (sx: number, sz: number) => number,
): Float32Array {
  const stride = piece.width * GROUND_STEPS + 1;
  const down = piece.height * GROUND_STEPS + 1;
  const normals = new Float32Array(stride * down * 3);

  // The step coordinates the whole region covers, which is where the faces stop.
  const lastX = region.width * GROUND_STEPS;
  const lastZ = region.height * GROUND_STEPS;
  const step = 1 / GROUND_STEPS;

  const fromX = piece.tx * GROUND_STEPS;
  const fromZ = piece.tz * GROUND_STEPS;

  for (let cz = 0; cz < down; cz++) {
    for (let cx = 0; cx < stride; cx++) {
      const sx = fromX + cx;
      const sz = fromZ + cz;

      // The slope either side, in tiles, taken over whatever is actually there:
      // at the edge of the region one side is missing and the other stands for
      // both, which is what the faces would have said.
      const west = Math.max(sx - 1, 0);
      const east = Math.min(sx + 1, lastX);
      const north = Math.max(sz - 1, 0);
      const south = Math.min(sz + 1, lastZ);

      const acrossX = (east - west) * step;
      const acrossZ = (south - north) * step;

      const slopeX = acrossX > 0 ? (groundAt(east, sz) - groundAt(west, sz)) / acrossX : 0;
      const slopeZ = acrossZ > 0 ? (groundAt(sx, south) - groundAt(sx, north)) / acrossZ : 0;

      // The upward normal of a surface with those two slopes.
      const length = Math.hypot(slopeX, 1, slopeZ) || 1;
      const index = (cz * stride + cx) * 3;
      normals[index] = -slopeX / length;
      normals[index + 1] = 1 / length;
      normals[index + 2] = -slopeZ / length;
    }
  }

  return normals;
}
